
import { useAuth0 } from "@auth0/auth0-react";
import { Avatar, Box, Chip } from "@mui/material";
import { DocumentEditor } from "@onlyoffice/document-editor-react";
import copy from "fast-copy";
import moment from "moment";
import { useSnackbar } from "notistack";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { LoadingSection } from "../../components";
import { config as Config } from "../../config";
import { IConfigOnlyOfficeEditor } from "../../interfaces/onlyOfficeEditor";
import { useAppSelector } from "../../redux";
import { useLazyGetDocumentByIdQuery, useLazyGetOnlyOfficeTokenQuery } from "../../redux/services";
import { AppRoutes } from "../../router/routes";
import { LocalStorageKeys, parseJwt } from "../../utils";

var onDocumentReady = function (event: any) {
    console.log("Document is loaded");
};

var onLoadComponentError = function (errorCode: any, errorDescription: any) {
    switch (errorCode) {
        case -1: // Unknown error loading component
            console.log(errorDescription);
            break;

        case -2: // Error load DocsAPI from http://documentserver/
            console.log(errorDescription);
            break;

        case -3: // DocsAPI is not defined
            console.log(errorDescription);
            break;
    }
};

const typeRegex = "android|avantgo|playbook|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od|ad)|iris|kindle|lge |maemo|midp|mmp|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\\/|plucker|pocket|psp|symbian|treo|up\\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino";

const OnlyOfficeEditor: React.FC<{ type: 'view' | 'edit', documentId?: string }> = ({ type = 'edit', ...props }) => {

    const { user } = useAuth0();
    const { userDetails } = useAppSelector(store => store.auth);
    const [searchParams] = useSearchParams();
    const { enqueueSnackbar } = useSnackbar();
    const [getOnlyOfficeToken] = useLazyGetOnlyOfficeTokenQuery();
    const [getDocumentAPI, getDocumentData] = useLazyGetDocumentByIdQuery();

    const token = localStorage.getItem(LocalStorageKeys.authToken) || "";
    const data = token ? parseJwt(token) : {};
    const documentId = props.documentId ? props.documentId : searchParams.get('document_id');

    const [config, setConfig] = React.useState<IConfigOnlyOfficeEditor>();

    const giveMeConfig = (documentData: any, token?: string): IConfigOnlyOfficeEditor => {

        const key = `${documentData._id ?? ""}`;
        const fileType = documentData?.file_name?.split('.')?.pop();

        let documentType: 'pdf' | 'word' | 'cell' | 'slide' = 'pdf';
        documentData.url = `${Config.api_url}only_office/download?document_id=${documentId}&tenant_id=${userDetails.org_id}`;

        if (['doc', 'docx'].includes(fileType)) {
            documentType = 'word'
        } else if (['xlsx', 'xls'].includes(fileType)) {
            documentType = 'cell'
        } else if (['ppt', 'pptx'].includes(fileType)) {
            documentType = 'slide'
        }

        let editorType = new RegExp(typeRegex, 'i').test(navigator.userAgent) ? 'mobile' : 'desktop';

        return {
            documentType,
            height: "100%",
            token,
            type: editorType,
            width: "100%",
            document: {
                fileType,
                key,
                referenceData: {
                    key,
                    fileKey: documentData.s3key,
                    instanceId: `${Config.api_url.replace('http://', '').replace('https://', '').replace('/', '')}`
                },
                info: {
                    owner: documentData.created_user,
                    uploaded: moment(documentData.created_time).format('ddd MMM D YYYY').toString(),
                    favorite: false
                },
                title: documentData.file_name,
                url: documentData.url,
                permissions: {
                    chat: true,
                    comment: true,
                    copy: true,
                    download: true,
                    edit: true,
                    fillForms: true,
                    modifyContentControl: true,
                    modifyFilter: true,
                    print: true,
                    review: true,
                    protect: true,
                    commentGroups: {},
                    reviewGroups: [],
                    userInfoGroups: []
                },
            },
            editorConfig: {
                actionLink: null,
                callbackUrl: `${Config.api_url}only_office/callback?tenant_id=${userDetails.org_id}`,
                // createUrl: `${Config.api_url}only_office/callback`,
                customization: {
                    comments: true,
                    feedback: true,
                    forcesave: false,
                    goback: {
                        url: window.location.href
                    },
                    submitForm: false
                },
                embedded: {
                    embedUrl: documentData.url,
                    saveUrl: documentData.url,
                    shareUrl: documentData.url,
                    toolbarDocked: "top"
                },
                lang: "en",
                mode: type,
                plugins: {
                    pluginsData: []
                },
                templates: [],
                user: {
                    group: "",
                    id: data?.sub ?? "",
                    image: user?.picture ?? "-",
                    name: data?.name
                }
            }
        }
    }

    const fetchDocuments = async () => {
        await getDocumentAPI({ document_id: documentId, url_type: "attachment" }).then(async (res) => {
            if (res.data) {
                const newConfig = giveMeConfig(copy(res.data));
                const response = await getOnlyOfficeToken({
                    id: `${documentId}`,
                    payload: {
                        create_url: ``,
                        callback_url: `${newConfig.editorConfig?.callbackUrl}`,
                        download_url: `${newConfig.document?.url}`,
                        display_type: `${newConfig.type}`,
                        document_type: `${newConfig.documentType}`,
                        goback_url: `${newConfig.editorConfig?.customization?.goback?.url}`,
                        key: `${newConfig.document?.key}`,
                        user_profile_url: `${newConfig.editorConfig?.user?.image}`,
                    }
                });
                setConfig(giveMeConfig(copy(res.data), response.data.token))
            } else {
                enqueueSnackbar('Something went wrong, Unable to prepare editor', { variant: "error" })
            }
        });
    };

    React.useEffect(() => {
        fetchDocuments();
        // eslint-disable-next-line
    }, [documentId, props.documentId]);

    return (
        <div style={{ height: "100%" }}>
            {(getDocumentData.isLoading || getDocumentData.isFetching || !config) && <LoadingSection message="Preparing..." />}

            {config && <>
                {type === 'view' && <Box sx={{
                    position: "absolute",
                    top: "6px",
                    left: 0,
                    right: 0,
                    display:"flex",
                    justifyContent:"center"
                }}>
                    {false && <Chip
                        avatar={<Avatar src="https://static-www.onlyoffice.com/v9.5.0/images/favicons01/favicon64.png" />}
                        size="small"
                        label={"Edit on OnlyOffice"}
                        color="secondary"
                        onClick={() => window.open(`${AppRoutes.officeEditor}?document_id=${props.documentId}`)}
                    />}
                </Box>}
                <DocumentEditor
                    id="docxEditor"
                    documentServerUrl={Config.onlyoffice_document_server}
                    config={config}
                    events_onDocumentReady={onDocumentReady}
                    onLoadComponentError={onLoadComponentError}
                />
            </>}
        </div>
    );
}

export default OnlyOfficeEditor;